// Objective: Maximize allocation, minimize understaff & unavailables, prefer stability, reduce external usage
// We maximize a linear function over variables:
//   + w_assign * sum(x_U_E_R)
//   - w_under  * sum(d_E_R)
//   - w_unavail * sum(x_U_E_R where user U unavailable for event E)
//   + w_keep * sum(x_U_E_R where (U is already assigned to E,R))
//   - w_extDistinct * sum(y_U for external U)
//   - w_extGate * sum(z_ext_E_R)
// Defaults: w_assign=1, w_under=20, w_unavail=100, w_keep=0.1, w_extDistinct=0.3, w_extGate=0.05
export function buildMaximizeAllocationObjective(model, params = {}) {
  const idx = model?.optimizationIndexes || {};
  const decisionVars = idx?.decisionVariables || {}; // { name -> { userKey, eventKey, roleKey } }
  const eventDecisionVars = idx?.eventRoleDecisionVariables || {}; // { d_E#_R# -> { eventKey, roleKey } }
  const userUnavailable = idx?.userUnavailable || {}; // { U# -> [E#] }
  const currentAssigneesByRole = idx?.currentAssigneesByRole || {}; // { E# -> { R# -> [U#] } }
  const externalUseVars = idx?.externalUseDecisionVariables || {}; // { y_U# -> { userKey } }
  const externalGatingVars = idx?.externalGatingDecisionVariables || {}; // { z_ext_E#_R# -> { eventKey, roleKey } }

  // Weights; allow aliases to match requested notation
  const weights = {
    assign: 1, // v_ij baseline utility
    unavailable: 100, // beta penalty when a_ij = 0
    understaff: 20, // gamma_e for d_E
    keepExisting: 0.1, // small bonus to keep existing assignments
    externalDistinct: 0.3, // penalty per distinct external used
    externalGate: 0.05, // small penalty for opening external gating on (E,R)
    ...(params?.weights || {}),
  };
  const v = weights.assign;
  const beta = weights.beta != null ? weights.beta : weights.unavailable;
  const gamma = weights.gamma != null ? weights.gamma : weights.understaff;
  const keep = weights.keep != null ? weights.keep : (weights.keepExisting != null ? weights.keepExisting : 0.1);
  const lambdaDistinct = weights.lambdaDistinct != null ? weights.lambdaDistinct : (weights.externalDistinct != null ? weights.externalDistinct : 0.3);
  const lambdaGate = weights.lambdaGate != null ? weights.lambdaGate : (weights.externalGate != null ? weights.externalGate : 0.05);

  const vars = [];
  const coeffs = [];

  // Assignment variables: coeff = v - beta * (1 - a_ij)
  // Where a_ij = 1 if user U is available for event E, else 0
  for (const name of Object.keys(decisionVars)) {
    const { userKey, eventKey, roleKey } = decisionVars[name] || {};
    const unavailableList = userUnavailable[userKey] || [];
    const isUnavailable = Array.isArray(unavailableList) && unavailableList.includes(eventKey);
    const a_ij = isUnavailable ? 0 : 1;
    // keep bonus if in currentAssigneesByRole[E][R] includes U
    const keepBonus = Array.isArray(currentAssigneesByRole?.[eventKey]?.[roleKey]) && currentAssigneesByRole[eventKey][roleKey].includes(userKey) ? keep : 0;
    const c = v - beta * (1 - a_ij) + keepBonus;
    vars.push(name);
    coeffs.push(c);
  }

  // Event-role slack variables: coeff = -gamma
  for (const name of Object.keys(eventDecisionVars)) {
    vars.push(name);
    coeffs.push(-gamma);
  }

  // External distinct users: penalty per y_U (external)
  for (const name of Object.keys(externalUseVars)) {
    vars.push(name);
    coeffs.push(-lambdaDistinct);
  }

  // External gating per (E,R): small penalty to avoid opening unless needed
  for (const name of Object.keys(externalGatingVars)) {
    vars.push(name);
    coeffs.push(-lambdaGate);
  }

  return {
    maximize: true,
    vars, 
    coeffs
  };
}
